import { MenuController } from '@ionic/angular';
import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MenuHelper {
  private activeRoute: string = '';
  private visibleRoutes: Set<string> = new Set(); // Empty set means all routes are visible by default

  constructor(
    protected menu: MenuController,
    protected router: Router
  ) {
    this.initRouteTracking();
  }

  /**
   * Initialize route tracking to update active route on navigation
   */
  private initRouteTracking() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.activeRoute = event.url;
      });
    this.activeRoute = this.router.url;
  }

  /**
   * Enable and open the menu
   */
  menuOpen(menuId?: string) {
    this.menu.enable(true, menuId);
    this.menu.open(menuId);
  }

  /**
   * Disable and close the menu
   */
  menuClose(menuId?: string) {
    this.menu.close(menuId);
    this.menu.enable(false, menuId);
  }

  /**
   * Toggle menu open/close state
   */
  async menuToggle(menuId?: string) {
    const isOpen = await this.menu.isOpen(menuId);
    if (isOpen) {
      this.menuClose(menuId);
    } else {
      this.menuOpen(menuId);
    }
  }

  /**
   * Enable menu without opening it
   */
  menuEnable(menuId?: string) {
    this.menu.enable(true, menuId);
  }

  /**
   * Disable menu
   */
  menuDisable(menuId?: string) {
    this.menu.enable(false, menuId);
  }

  /**
   * Check if menu is open
   */
  async isMenuOpen(menuId?: string): Promise<boolean> {
    return await this.menu.isOpen(menuId);
  }

  /**
   * Check if menu is enabled
   */
  async isMenuEnabled(menuId?: string): Promise<boolean> {
    return await this.menu.isEnabled(menuId);
  }

  /**
   * Get current active route
   */
  getActiveRoute(): string {
    return this.activeRoute;
  }

  /**
   * Check if a route is currently active
   */
  isRouteActive(route: string): boolean {
    return this.activeRoute === route || this.activeRoute.startsWith(route + '/');
  }

  /**
   * Get CSS classes for active/inactive menu item
   */
  getNavItemClass(route: string, activeClass: string = 'text-white bg-[#E91447] font-medium', inactiveClass: string = 'text-gray-700 hover:bg-gray-50'): string {
    return this.isRouteActive(route) ? activeClass : inactiveClass;
  }

  /**
   * Get CSS classes for menu item with hide support
   * Returns 'hidden' class if shouldHide is true, otherwise returns normal classes
   */
  getNavItemClassWithVisibility(route: string, shouldHide: boolean = false, activeClass: string = 'text-white bg-[#E91447] font-medium', inactiveClass: string = 'text-gray-700 hover:bg-gray-50'): string {
    if (shouldHide) {
      return 'hidden';
    }
    return this.isRouteActive(route) ? activeClass : inactiveClass;
  }

  /**
   * Check if a menu item should be visible based on route
   * Useful for conditional rendering with *ngIf
   */
  shouldShowMenuItem(route: string, condition: boolean = true): boolean {
    // If visibleRoutes is empty, show all items (default behavior)
    // if (this.visibleRoutes.size === 0) {
    //   return condition;
    // }
    // Otherwise, only show items in the visibleRoutes set
    return condition && this.visibleRoutes.has(route);
  }

  /**
   * Set which menu items should be visible
   * @param routes Array of route paths that should be visible. Empty array means show all.
   */
  setVisibleMenuItems(routes: string[]) {
    if (routes.length === 0) {
      this.visibleRoutes.clear();
    } else {
      this.visibleRoutes = new Set(routes);
    }
  }

  /**
   * Clear menu visibility restrictions (show all items)
   */
  clearMenuVisibilityRestrictions() {
    this.visibleRoutes.clear();
  }

  /**
   * Navigate to a route and update active route
   */
  navigateTo(route: string) {
    this.activeRoute = route;
    this.router.navigate([route]);
  }

  /**
   * Close menu after navigation
   */
  async navigateToAndCloseMenu(route: string, menuId?: string) {
    this.navigateTo(route);
    await this.menu.close(menuId);
  }
}
